/**
 * Standardized industry taxonomy.
 *
 * `BrandStrategy.industry` was free-text (≤80 chars) until 2026-06 — which made the
 * cross-tenant authority cache (`CACHE#AUTHORITY / COUNTRY#<c>#INDUSTRY#<slug>`, shared
 * by Earned Media + News Watch) nearly never hit: "B2B SaaS" / "b2b saas" / "SaaS B2B"
 * are three different slugs. Forcing a fixed enum makes the cache key `(country, industryCode)`
 * so brands in the same vertical actually share the media list.
 *
 * The list is deliberately LARGE/horizontal (~24 buckets) — precision comes from the brand
 * keywords, not from a deep industry tree. No "Autre / free-text" bucket by design: free text
 * reintroduces the cache miss and degrades UX (see plan §3).
 */

export const INDUSTRY_CODES = [
  'saas_b2b',
  'ecommerce',
  'retail',
  'restauration',
  'tourism_hospitality',
  'health',
  'legal_services',
  'real_estate',
  'manufacturing',
  'construction',
  'finance_banking',
  'insurance',
  'media_publishing',
  'marketing_agency',
  'education_training',
  'energy',
  'transport_logistics',
  'automotive',
  'fashion_beauty',
  'sports_leisure',
  'b2b_services',
  'b2c_services',
  'consumer_tech',
  'public_sector',
] as const;

export type IndustryCode = (typeof INDUSTRY_CODES)[number];

/** French display labels — the product UI is French-first. */
export const INDUSTRY_LABELS_FR: Record<IndustryCode, string> = {
  saas_b2b: 'SaaS B2B',
  ecommerce: 'E-commerce',
  retail: 'Commerce de détail',
  restauration: 'Restauration',
  tourism_hospitality: 'Tourisme & hôtellerie',
  health: 'Santé',
  legal_services: 'Services juridiques',
  real_estate: 'Immobilier',
  manufacturing: 'Industrie & production',
  construction: 'Construction & BTP',
  finance_banking: 'Finance & banque',
  insurance: 'Assurance',
  media_publishing: 'Média & édition',
  marketing_agency: 'Marketing & agences',
  education_training: 'Éducation & formation',
  energy: 'Énergie',
  transport_logistics: 'Transport & logistique',
  automotive: 'Automobile',
  fashion_beauty: 'Mode & beauté',
  sports_leisure: 'Sport & loisirs',
  b2b_services: 'Services aux entreprises',
  b2c_services: 'Services aux particuliers',
  consumer_tech: 'Tech grand public',
  public_sector: 'Secteur public',
};

/** English display labels — used when the UI language is English. */
export const INDUSTRY_LABELS_EN: Record<IndustryCode, string> = {
  saas_b2b: 'B2B SaaS',
  ecommerce: 'E-commerce',
  retail: 'Retail',
  restauration: 'Food & restaurants',
  tourism_hospitality: 'Tourism & hospitality',
  health: 'Health',
  legal_services: 'Legal services',
  real_estate: 'Real estate',
  manufacturing: 'Manufacturing & industry',
  construction: 'Construction & building',
  finance_banking: 'Finance & banking',
  insurance: 'Insurance',
  media_publishing: 'Media & publishing',
  marketing_agency: 'Marketing & agencies',
  education_training: 'Education & training',
  energy: 'Energy',
  transport_logistics: 'Transport & logistics',
  automotive: 'Automotive',
  fashion_beauty: 'Fashion & beauty',
  sports_leisure: 'Sports & leisure',
  b2b_services: 'B2B services',
  b2c_services: 'Consumer services',
  consumer_tech: 'Consumer tech',
  public_sector: 'Public sector',
};

/** Locale-keyed label maps. Add a locale here + the map above to support it. */
export const INDUSTRY_LABELS: Record<'fr' | 'en', Record<IndustryCode, string>> = {
  fr: INDUSTRY_LABELS_FR,
  en: INDUSTRY_LABELS_EN,
};

/** Label map for a UI language (defaults to French). */
export function industryLabels(lang?: string): Record<IndustryCode, string> {
  return lang === 'en' ? INDUSTRY_LABELS_EN : INDUSTRY_LABELS_FR;
}

const INDUSTRY_CODE_SET: ReadonlySet<string> = new Set(INDUSTRY_CODES);

/** Type guard — true when the value is a known enum code. */
export function isIndustryCode(value: unknown): value is IndustryCode {
  return typeof value === 'string' && INDUSTRY_CODE_SET.has(value);
}

/**
 * Human-facing French label for an industry value. Maps a known code to its label;
 * passes through anything else (legacy free-text, or a `industryLegacy` fallback)
 * unchanged. Use this ANYWHERE the industry is shown to a user or fed to an LLM —
 * never render the raw code (e.g. reports, prompts). Returns '' for empty input.
 */
export function industryLabel(value: string | undefined | null, lang?: string): string {
  if (!value) return '';
  return isIndustryCode(value) ? industryLabels(lang)[value] : value;
}
